import React from 'react';
import { Organization } from 'common/api/crm/models/Organization';
import { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import { FieldSpacer } from '../../../../../components/fields/FieldSpacer';
import { Btn } from '../../../../../components/btn/Btn';
import { useValidation } from '../../../../../modules/form-validation/useValidation';
import { FormikSelectField } from 'components/fields/FormikSelectField';
import { FormikTextField } from 'components/fields/FormikTextField';
import {
    addressLine2Validator,
    cityValidator,
    stateValidator,
    organizationZipCodeValidator,
    orgAddressLine1Validator,
    orgEinNumberValidator,
    orgPhoneNumberValidator,
    organizationPostalCodeValidator,
    provinceValidator,
} from 'common/modules/form-validation/validators';
import { stateOptions } from 'common/modules/form-validation/utils/stateOptions';
import { OrganizationWebsiteFieldV2 } from '../components/OrganizationWebsiteFieldV2';
import {
    selectOrganization,
    selectProgressBarRef,
    setOrganizationData,
    updateCurrentStepBy,
} from 'common/features/store/duck/ui/org-enrollment-v2';
import { useRootSelector } from 'common/features/featuresReducer';
import { useDispatch, useSelector } from 'react-redux';
import { flattenObject } from 'common/utils/flattenObject';
import { ContentfulSubheading } from '../components/ContentfulSubheading';
import MaskedInput from 'react-text-mask';
import { MASK_PHONE_US } from 'common/modules/mask-text/masks';
import { scrollTo } from '../utils/ScrollTo';
import { orgFundraisingPurposeValidator } from 'features/coordinator/enrollment/steps/validators';
import { IS_FLIPGIVE } from 'common/env';
import { provinceOptions } from 'common/modules/form-validation/utils/provinceOptions';
import { logOrgEnrollmentV2 } from 'config/analytics';
import { SmsOptInCheckbox } from 'features/coordinator/components/SmsOptInCheckbox';

export const EnrollOrganizationDetails = () => {
    const isPostalCode = IS_FLIPGIVE;
    const dispatch = useDispatch<any>();
    const organization = useRootSelector(selectOrganization);
    const progressBarRef = useSelector(selectProgressBarRef);
    const [smsOptedIn, setSmsOptedIn] = useState(organization?.smsOptedIn ?? false);

    const { address, phoneNumber, website, fundraisingPurpose, ein } = organization || {
        address: {},
        phoneNumber: '',
        website: '',
        fundraisingPurpose: '',
        ein: '',
    };
    const validateAndSubmit =
        useValidation<Partial<Organization>>((errors) => Object.keys(flattenObject(errors))) ||
        ((formikProps) => (e) => {
            e.preventDefault();
            formikProps.handleSubmit(e);
        });

    const updateOrgData = useCallback(
        async (values: Partial<Organization>, stepIncrement: number) => {
            dispatch(
                setOrganizationData({
                    address: values.address,
                    phoneNumber: values.phoneNumber,
                    website: values.website,
                    fundraisingPurpose: values.fundraisingPurpose,
                    ein: values.ein,
                    smsOptedIn,
                })
            );
            scrollTo(progressBarRef);
            dispatch(updateCurrentStepBy(stepIncrement));
        },
        [dispatch, progressBarRef, smsOptedIn]
    );

    const _onSubmit = useCallback(
        (values: Partial<Organization>) => {
            logOrgEnrollmentV2('org_enrollment:organization_details_submitted');
            updateOrgData(values, 1);
        },
        [updateOrgData]
    );

    const onBackClicked = useCallback(
        async (values: Partial<Organization>) => {
            updateOrgData(values, -1);
        },
        [updateOrgData]
    );

    // Using onChange instead of MaskedInput because react-text-mask has a bug where backspace doesn't work over literal space characters in the mask.
    const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        const pattern = [/[A-Z]/, /\d/, /[A-Z]/, /\d/, /[A-Z]/, /\d/];
        let result = '';
        for (let i = 0; i < raw.length && i < 6; i++) {
            if (pattern[i].test(raw[i])) {
                result += raw[i];
            } else {
                break;
            }
        }
        e.target.value = result.length > 3 ? result.slice(0, 3) + ' ' + result.slice(3) : result;
    };

    const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);
    };

    return (
        <div>
            <ContentfulSubheading attribute="step2Subtitle" />
            <Formik
                initialValues={{ address, phoneNumber, website, fundraisingPurpose, ein }}
                onSubmit={_onSubmit}
            >
                {(formikProps) => (
                    <Form onSubmit={validateAndSubmit(formikProps)}>
                        <FormikTextField
                            label="Organization Address"
                            name="address.address1"
                            placeholder={"Enter your organization's address"}
                            validate={orgAddressLine1Validator}
                        />
                        <FieldSpacer />
                        <FormikTextField
                            label="Organization Address Line 2 (Optional)"
                            name="address.address2"
                            placeholder="Suite, unit, or attention"
                            validate={addressLine2Validator}
                        />
                        <FieldSpacer />
                        <div className="flex md:flex-row flex-col">
                            <div className="flex-1 md:mr-3">
                                <FormikTextField
                                    label="City"
                                    name="address.city"
                                    placeholder="Enter city"
                                    validate={cityValidator}
                                />
                            </div>
                            <div className="flex flex-row">
                                <div className="flex-1 md:w-36 mr-3 w-auto mt-6 md:mt-0">
                                    <FormikSelectField
                                        label={isPostalCode ? 'Province' : 'State'}
                                        name="address.state"
                                        className={isPostalCode ? 'truncate !pr-[22px]' : ''}
                                        // @ts-ignore
                                        placeholder={isPostalCode ? 'Select' : 'Select state'}
                                        validate={isPostalCode ? provinceValidator : stateValidator}
                                    >
                                        <option value="">
                                            {isPostalCode ? 'Select' : 'Select state'}
                                        </option>
                                        {(isPostalCode ? provinceOptions : stateOptions).map(
                                            (o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            )
                                        )}
                                    </FormikSelectField>
                                </div>
                                <div className="flex-1 md:w-36 w-full mt-6 md:mt-0">
                                    {isPostalCode ? (
                                        <FormikTextField
                                            label="Postal Code"
                                            name="address.zipCode"
                                            placeholder="Ex: A1A 1A1"
                                            validate={organizationPostalCodeValidator}
                                            maxLength={7}
                                            onChange={handlePostalCodeChange}
                                        />
                                    ) : (
                                        <FormikTextField
                                            label="ZIP Code"
                                            name="address.zipCode"
                                            placeholder="Enter ZIP code"
                                            validate={organizationZipCodeValidator}
                                            maxLength={5}
                                            onChange={handleZipCodeChange}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <FieldSpacer />
                        <div className="flex flex-col w-full">
                            <MaskedInput
                                mask={MASK_PHONE_US}
                                guide={false}
                                render={(ref, _props) => (
                                    <FormikTextField
                                        {..._props}
                                        name="phoneNumber"
                                        inputRef={ref}
                                        type="tel"
                                        placeholder="Ex: (555) 555-5555"
                                        label="Organization Phone Number"
                                        validate={orgPhoneNumberValidator}
                                    />
                                )}
                            />
                            <p className="text-xs text-grey-medium pt-1">
                                Our team will contact your organization at this number if further
                                information is needed.
                            </p>
                            <SmsOptInCheckbox checked={smsOptedIn} onChange={setSmsOptedIn} />
                        </div>
                        <FieldSpacer />
                        <div>
                            <OrganizationWhatAreYouFundRaisingFor />
                        </div>
                        <FieldSpacer />
                        <div>
                            <OrganizationOptionalEinNumber isPostalCode={isPostalCode} />
                        </div>
                        <FieldSpacer />
                        <span>
                            <OrganizationWebsiteFieldV2 label="Website or Social Media Page" />
                        </span>
                        <FieldSpacer />
                        <div className="flex">
                            <Btn
                                isSecondary
                                onClick={() => onBackClicked(formikProps.values)}
                                className="sm:w-1/3 w-1/2 mr-3"
                            >
                                Back
                            </Btn>
                            <Btn
                                type="submit"
                                className="sm:w-1/3 w-1/2"
                                disabled={!formikProps.isValid}
                            >
                                Continue
                            </Btn>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

const OrganizationWhatAreYouFundRaisingFor = () => (
    <>
        <FormikTextField
            label="What Are You Fundraising For?"
            name="fundraisingPurpose"
            placeholder=""
            validate={orgFundraisingPurposeValidator}
        />
        <p className="text-xs text-grey-medium pt-1">
            E.g., travel fees, tuition reduction, team/group equipment, building maintenance, etc.
        </p>
    </>
);

const OrganizationOptionalEinNumber = ({ isPostalCode }: { isPostalCode: boolean }) => (
    <FormikTextField
        label={
            <p>
                {isPostalCode
                    ? 'CRA Charitable Registration Number or Business Number '
                    : '501c3 Number (EIN) '}
                <span className="text-grey-medium font-normal">(Optional)</span>
            </p>
        }
        name="ein"
        placeholder=""
        validate={orgEinNumberValidator}
    />
);
